<template>
    <BaseCard padding="none" radius="lgr" as="section" class="flex flex-col gap-6 p-4 text-ink md:p-10" :aria-label="t('personDetail.tabs.dynamic')">
        <h2 class="display-small-serif">{{ t("personDetail.dynamicHeading", { name: firstName }) }}</h2>
        <div class="grid grid-cols-[minmax(0,1fr)_auto_minmax(0,1fr)] items-center gap-2 rounded-lgr bg-surface px-3 py-10 sm:gap-3 sm:px-4 md:px-10">
            <div class="body-small-semibold min-w-0 break-words">
                <p>{{ t("teamPage.you") }}</p>
                <p v-if="comparison.viewer_archetype">{{ comparison.viewer_archetype }}</p>
            </div>
            <PairPerspectiveMark :viewer-archetype="comparison.viewer_archetype" :colleague-archetype="comparison.colleague_archetype" class="h-12 w-16 sm:h-16 sm:w-24" />
            <div class="body-small-semibold min-w-0 break-words text-right">
                <p>{{ firstName }}</p>
                <p v-if="comparison.colleague_archetype">{{ comparison.colleague_archetype }}</p>
            </div>
        </div>
        <dl v-if="comparison.highlights.length" class="flex flex-col gap-5">
            <div v-for="(highlight, index) in comparison.highlights" :key="`${highlight.dimension_id}-${index}`" class="grid gap-2 md:grid-cols-[11.5rem_minmax(0,1fr)] md:gap-4">
                <dt class="body-small-semibold flex items-start gap-2">
                    <i class="bi" :class="index === 0 ? 'bi-intersect' : 'bi-subtract'" aria-hidden="true" />
                    {{ index === 0 ? firstHighlightLabel : t("pairDetail.similarity") }}
                </dt>
                <dd class="body text-ink">{{ highlight.text }}</dd>
            </div>
        </dl>
        <div class="grid gap-3 border-t border-border pt-6 md:grid-cols-[11.5rem_minmax(0,1fr)] md:gap-4">
            <h3 class="body-small-semibold flex items-start gap-2"><i class="bi bi-stars" aria-hidden="true" />{{ t("pairDetail.notes") }}</h3>
            <div class="flex min-w-0 flex-col gap-4">
                <span class="sr-only" role="status" aria-live="polite" aria-atomic="true">{{ generatingAnnouncement }}</span>
                <template v-if="advice && !isGenerating && !adviceError">
                    <ul v-if="hasStructuredAdvice" class="body ml-4 list-disc space-y-2">
                        <li>{{ advice.alignment }}</li>
                        <li>{{ advice.friction }}</li>
                        <li>{{ advice.action }}</li>
                    </ul>
                    <p v-else class="body whitespace-pre-line">{{ advice.interpretation }}</p>
                    <BaseAccordion v-if="advice.evidence.length" :accordion-ids="['evidence']" trigger-class="body-small text-ink-muted px-0 py-2" content-class="pb-3">
                        <template #trigger-evidence
                            ><span>{{ t("pairDetail.evidence", { count: advice.evidence.length }) }}</span></template
                        >
                        <template #content-evidence>
                            <ul class="flex flex-col gap-4 pt-3">
                                <li v-for="source in advice.evidence" :key="source.id" class="flex flex-col gap-1">
                                    <p class="body-small-semibold">{{ source.label }}</p>
                                    <p class="body-small text-ink-muted">{{ source.content }}</p>
                                </li>
                            </ul>
                        </template>
                    </BaseAccordion>
                </template>
                <p v-else-if="isGenerating && !retrying" class="body text-ink-muted">{{ t("pairDetail.generating") }}</p>
                <div v-else-if="adviceError || retrying" class="flex flex-col items-start gap-3">
                    <p v-if="retrying" class="body text-ink-muted">{{ t("pairDetail.generating") }}</p>
                    <p v-else role="alert" class="body-small">{{ adviceError }}</p>
                    <BaseButton theme="neutral" size="sm" :disabled="retrying" @click="retryAdvice">{{ t("pairDetail.retry") }}</BaseButton>
                </div>
                <div class="flex flex-wrap items-center gap-3">
                    <BaseButton theme="tertiary" size="sm" :disabled="isStartingChat" @click="discussDynamic">
                        {{ isStartingChat ? t("pairDetail.opening") : t("pairDetail.discuss") }}<i class="bi bi-arrow-right" aria-hidden="true" />
                    </BaseButton>
                    <span class="details flex items-center gap-2 text-ink-muted"><i class="bi bi-lock" aria-hidden="true" />{{ t("pairDetail.private") }}</span>
                </div>
                <p v-if="chatError" role="alert" class="body-small">{{ chatError }}</p>
            </div>
        </div>
        <p class="details text-ink-muted">{{ t("pairDetail.assessmentSource") }}</p>
    </BaseCard>
</template>
<script setup lang="ts">
import type { PairAdviceResponse, PairComparison } from "~vue/api/generated/pair-perspective";
import BaseAccordion from "~vue/components/BaseAccordion.vue";
import BaseButton from "~vue/components/BaseButton.vue";
import BaseCard from "~vue/components/BaseCard.vue";
import { useLoadingAnnouncement } from "~vue/composables/useLoadingAnnouncement";
import PairPerspectiveMark from "~vue/Connections/PairPerspectiveMark.vue";
import { computed, ref, watch } from "vue";
import { useI18n } from "vue-i18n";

const props = defineProps<{
    comparison: PairComparison;
    advice: PairAdviceResponse | null;
    isGenerating: boolean;
    adviceError: string | null;
    isStartingChat: boolean;
    chatError: string | null;
}>();
const emit = defineEmits<{ retry: []; discuss: [message: string] }>();

const { t } = useI18n();
const retrying = ref(false);
const generatingAnnouncement = useLoadingAnnouncement(
    () => props.isGenerating,
    () => (retrying.value ? t("pairDetail.tryingAgain") : t("pairDetail.generating")),
);
watch(
    () => props.isGenerating,
    (generating) => {
        if (!generating) {
            retrying.value = false;
        }
    },
);
function retryAdvice() {
    retrying.value = true;
    emit("retry");
}

const firstName = computed(() => props.comparison.colleague_name.trim().split(/\s+/)[0]);
const hasStructuredAdvice = computed(() => !!(props.advice?.alignment && props.advice.friction && props.advice.action));
const firstHighlightLabel = computed(() =>
    props.comparison.dimensions.some(
        (dimension) => dimension.lenses.some((lens) => lens.similarity === "different") || dimension.viewer.binned_score !== dimension.colleague.binned_score,
    )
        ? t("pairDetail.difference")
        : t("pairDetail.pattern"),
);
function discussDynamic() {
    const name = props.comparison.colleague_name;
    const message =
        props.advice && !props.adviceError
            ? t("connections.pairPerspective.advice.discussMessageWithSuggestion", { name, suggestion: props.advice.interpretation })
            : t("connections.pairPerspective.advice.discussMessage", { name });
    emit("discuss", message);
}
</script>
